import {
  createAsyncThunk,
  ActionReducerMapBuilder,
  EntityState,
  createAction,
} from '@reduxjs/toolkit';
import { classToPlain } from 'class-transformer';

import { AppError } from '@vk-hr-tek/core/error';
import { showCustomNotification } from '@vk-hr-tek/core/notifications';
import { Filter } from '@vk-hr-tek/core/filter';

import {
  PostData,
  NewsData,
  Widget,
  PinnedItems,
  DashboardService,
  GetAccountWidgetPostsFeedQuery,
  GetAccountWidgetPostsFeedParams,
  GetAccountWidgetPostsFeedResponse,
  GetAccountWidgetParams,
  GetAccountWidgetPostsFeedAvailableFiltersQuery,
} from '@app/gen/dashboard';
import { ThunkExtra } from '@app/store';
import { Entity } from '@app/types';
import { CompanyEventsStat, CompanyGroupUsers } from '@app/gen/settings';

import { SettingsState, SettingsWithRootState } from '../../../slice';

const setFeedPostsFilters = createAction<Filter[]>('feedPosts/setFilters');

type GetFeedPostsSettingsParams = GetAccountWidgetPostsFeedQuery &
  GetAccountWidgetPostsFeedParams;

type GetFeedPostsAvailableFiltersParams = {
  account_id: string;
  widget_id: string;
  type: GetAccountWidgetPostsFeedAvailableFiltersQuery['type'];
  company_id?: string;
};

export const getFeedPostsAvailableFilters = createAsyncThunk<
  Filter[],
  GetFeedPostsAvailableFiltersParams,
  ThunkExtra<SettingsWithRootState>
>(
  'feedPosts/getFeedPostsAvailableFilters',
  async (params, { rejectWithValue, extra: { inject }, dispatch }) => {
    try {
      const service = inject(DashboardService);
      const {
        account_id: accountId,
        widget_id: widgetId,
        type,
        company_id: companyId,
      } = params;

      const response = await service.getAccountWidgetPostsFeedAvailableFilters({
        params: {
          account_id: accountId,
          widget_id: widgetId,
        },
        query: {
          type,
          ...(companyId && { company_id: companyId }),
        },
      });

      const filters = response.filters;

      dispatch(setFeedPostsFilters(filters));

      return filters;
    } catch (err) {
      return rejectWithValue(classToPlain(err) as AppError);
    }
  },
);

export const getFeedPostsSettings = createAsyncThunk<
  GetAccountWidgetPostsFeedResponse,
  GetFeedPostsSettingsParams,
  ThunkExtra<SettingsWithRootState>
>(
  'feedPosts/getFeedPostsSettings',
  async (params, { rejectWithValue, extra: { inject } }) => {
    try {
      const service = inject(DashboardService);
      const {
        account_id: accountId,
        widget_id: widgetId,
        company_id: companyId,
        type,
        ...query
      } = params;

      const response = await service.getAccountWidgetPostsFeed({
        params: {
          account_id: accountId,
          widget_id: widgetId,
        },
        query: {
          company_id: companyId,
          type,
          ...query,
        },
      });

      return response;
    } catch (err) {
      return rejectWithValue(classToPlain(err) as AppError);
    }
  },
);

export const getWidget = createAsyncThunk<
  Widget,
  GetAccountWidgetParams,
  ThunkExtra<SettingsWithRootState>
>(
  'feedPosts/getWidget',
  async (getWidgetParams, { rejectWithValue, extra: { inject } }) => {
    try {
      const service = inject(DashboardService);

      const result = await service.getAccountWidget({
        params: getWidgetParams,
      });

      return result;
    } catch (err) {
      return rejectWithValue(classToPlain(err) as AppError);
    }
  },
);

export const updateFeedPostsSettings = createAsyncThunk<
  void,
  {
    values: PinnedItems;
    params: GetAccountWidgetPostsFeedParams & {
      company_id?: string;
    };
    actions: {
      resolve: () => void;
    };
  },
  ThunkExtra<SettingsWithRootState>
>(
  'feedPosts/updateFeedPostsSettings',
  async (
    { values, params, actions },
    { rejectWithValue, extra: { inject }, dispatch },
  ) => {
    try {
      const service = inject(DashboardService);
      const {
        account_id: accountId,
        widget_id: widgetId,
        company_id: companyId,
      } = params;

      await service.updateAccountWidgetPostsFeed({
        params: {
          account_id: accountId,
          widget_id: widgetId,
        },
        query: companyId ? { company_id: companyId } : {},
        body: values,
      });

      actions.resolve();
      dispatch(
        showCustomNotification([
          {
            title: 'Виджет успешно изменен',
            message: 'Лента публикаций',
            type: 'info',
          },
        ]),
      );
    } catch (err) {
      dispatch(
        showCustomNotification([
          {
            title: 'Не удалось сохранить виджет',
            message: 'Попробуйте еще раз позже',
            type: 'error',
          },
        ]),
      );
      return rejectWithValue(classToPlain(err) as AppError);
    }
  },
);

export const settingsFeedPostsReducers = (
  builder: ActionReducerMapBuilder<
    EntityState<Entity<CompanyEventsStat | CompanyGroupUsers>, string> &
      SettingsState
  >,
) => {
  builder.addCase(getFeedPostsSettings.pending, (state, { meta }) => {
    const type = meta.arg.type;
    state.feedPostsDetail.settings.isExceptionWidget = true;

    if (type === 'posts') {
      state.feedPostsDetail.postsList.status = 'loading';
      state.feedPostsDetail.postsList.items = [];
      state.feedPostsDetail.postsList.total = 0;
    } else if (type === 'news') {
      state.feedPostsDetail.newsList.status = 'loading';
      state.feedPostsDetail.newsList.items = [];
      state.feedPostsDetail.newsList.total = 0;
    }
  });

  builder.addCase(
    getFeedPostsSettings.fulfilled,
    (state, { payload, meta }) => {
      const type = meta.arg.type;
      const items = payload.items || [];
      const { news_id: newsId, post_id: postId } = payload.pinned_items || {};

      state.feedPostsDetail.settings.pinnedPostId = postId || '';
      state.feedPostsDetail.settings.pinnedNewsId = newsId || '';
      state.feedPostsDetail.settings.isExceptionWidget =
        !!payload.company_content_id;
      state.feedPostsDetail.settings.hasParent = !!payload.source_widget_id;

      if (type === 'posts') {
        state.feedPostsDetail.postsList.status = 'complete';
        state.feedPostsDetail.postsList.items = items.filter(
          (item): item is PostData => 'community' in item,
        );
        state.feedPostsDetail.postsList.total = payload.total || 0;
      } else if (type === 'news') {
        state.feedPostsDetail.newsList.status = 'complete';
        state.feedPostsDetail.newsList.items = items.filter(
          (item): item is NewsData => 'account' in item,
        );
        state.feedPostsDetail.newsList.total = payload.total || 0;
      }
    },
  );

  builder.addCase(
    getFeedPostsSettings.rejected,
    (state, { payload, error, meta }) => {
      const type = meta.arg.type;
      const appError =
        payload ||
        ({
          info: (error && error.message) || 'Что-то пошло не так',
          status: 500,
          source: 'client',
          title: 'Internal client error',
        } as AppError);

      if (type === 'posts') {
        state.feedPostsDetail.postsList.status = 'failed';
        state.feedPostsDetail.postsList.items = [];
        state.feedPostsDetail.postsList.total = 0;
      } else if (type === 'news') {
        state.feedPostsDetail.newsList.status = 'failed';
        state.feedPostsDetail.newsList.items = [];
        state.feedPostsDetail.newsList.total = 0;
      }

      state.feedPostsDetail.settings.error = appError;
    },
  );

  builder.addCase(getWidget.fulfilled, (state, { payload }) => {
    state.feedPostsDetail.widget = payload;
  });

  builder.addCase(setFeedPostsFilters, (state, { payload }) => {
    state.feedPostsDetail.filters = payload;
  });

  builder.addCase(updateFeedPostsSettings.pending, (state) => {
    state.feedPostsDetail.settings.status = 'loading';
    state.feedPostsDetail.settings.error = null;
  });

  builder.addCase(updateFeedPostsSettings.fulfilled, (state, { meta }) => {
    state.feedPostsDetail.settings.status = 'complete';
    state.feedPostsDetail.settings.error = null;
    state.feedPostsDetail.settings.pinnedPostId = meta.arg.values.post_id || '';
    state.feedPostsDetail.settings.pinnedNewsId = meta.arg.values.news_id || '';
  });

  builder.addCase(
    updateFeedPostsSettings.rejected,
    (state, { payload, error }) => {
      state.feedPostsDetail.settings.status = 'failed';
      state.feedPostsDetail.settings.error =
        payload ||
        ({
          info: (error && error.message) || 'Что-то пошло не так',
          status: 500,
          source: 'client',
          title: 'Internal client error',
        } as AppError);
    },
  );
};
